<script setup>
// SiteUp (Cartões de Apoio): a flechinha sob o cabeçalho da conversa. Fechada
// custa zero: o endpoint só é chamado na primeira vez que o atendente expande.
// Sem contexto na resposta, a barra some em vez de abrir um painel vazio.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocale } from 'shared/composables/useLocale';
import { isValidURL } from 'dashboard/helper/URLHelper';
import LeadContextAPI from 'dashboard/api/leadContext';

const props = defineProps({
  conversation: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const { resolvedLocale } = useLocale();

const isOpen = ref(false);
const isLoading = ref(false);
const isLoaded = ref(false);
const hasError = ref(false);
const context = ref(null);
// Cada busca leva um número. Trocar de conversa invalida a anterior, então a
// resposta que chegar fora de ordem é descartada em vez de gravar o contexto do
// lead A sob o cabeçalho do lead B.
let requestId = 0;

const conversationId = computed(
  () => props.conversation?.display_id ?? props.conversation?.id
);

// Antes da primeira abertura ninguém sabe se há contexto, então a barra aparece.
// Depois da resposta, conversa sem contexto esconde o componente inteiro. Falha
// não esconde: mantém a barra com o aviso e o "tentar de novo".
// Exceto se o atendente ESTA com o painel aberto: sumir debaixo do clique
// parece bug (achado do Lucas na v951). Aberto e vazio, ele diz que nao ha
// origem registrada; fechado, some como antes.
const isVisible = computed(
  () =>
    Boolean(conversationId.value) &&
    (!isLoaded.value || Boolean(context.value?.has_context) || isOpen.value)
);

const isEmpty = computed(() => isLoaded.value && !context.value?.has_context);

const origin = computed(() => context.value?.origin ?? {});
const activity = computed(() => context.value?.activity ?? []);
const journey = computed(() => context.value?.journey ?? []);

const ORIGIN_CHANNEL_LABELS = {
  meta_ad: 'LEAD_ORIGIN.CHANNEL.META_AD',
  meta_post: 'LEAD_ORIGIN.CHANNEL.META_POST',
};

const originChannel = computed(() => {
  const { channel } = origin.value;
  if (!channel) return '';
  return ORIGIN_CHANNEL_LABELS[channel]
    ? t(ORIGIN_CHANNEL_LABELS[channel])
    : channel;
});

// A URL do anúncio vem de payload externo (referral da Meta): só vira link com
// esquema http(s) reconhecido, senão o título aparece como texto.
const adUrl = computed(() => {
  const url = origin.value.ad_url;
  return url && isValidURL(url) ? url : '';
});

const utmEntries = computed(() => Object.entries(origin.value.utm ?? {}));
const hasOrigin = computed(() => Object.keys(origin.value).length > 0);

const formatDate = value => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString(resolvedLocale.value, {
    day: '2-digit',
    month: '2-digit',
  });
};

const membership = computed(() => context.value?.membership ?? {});
const membershipPlans = computed(() => membership.value?.planos ?? []);
const hasMembership = computed(() => membershipPlans.value.length > 0);

const membershipStatus = plan => {
  if (plan.vitalicio || plan.status === 'vitalicio') {
    return t('LEAD_CONTEXT.MEMBERSHIP.LIFETIME');
  }
  if (plan.status === 'expirado') {
    return t('LEAD_CONTEXT.MEMBERSHIP.EXPIRED');
  }
  return t('LEAD_CONTEXT.MEMBERSHIP.ACTIVE');
};

const membershipExpiry = plan => {
  if (plan.vitalicio || !plan.expiracao) return '';
  return t('LEAD_CONTEXT.MEMBERSHIP.UNTIL', {
    date: formatDate(plan.expiracao),
  });
};

const purchases = computed(() => context.value?.purchases ?? {});
const purchaseRows = computed(() => purchases.value?.compras ?? []);
const hasPurchases = computed(() => purchaseRows.value.length > 0);

const disc = computed(() => context.value?.disc ?? {});
const hasDisc = computed(() => Boolean(disc.value.type));

const guia = computed(() => context.value?.guia ?? {});
const guiaHistorico = computed(() => guia.value?.historico ?? []);
const hasGuia = computed(
  () => Boolean(guia.value?.icp?.slug) || guiaHistorico.value.length > 0
);

// Hermes (central de jornada): compras de todas as lojas, origem no site e
// páginas visitadas. O backend já filtra o contrato (nunca cartão nem
// documento); aqui só formata. Bloco sem dado não aparece.
const hermes = computed(() => context.value?.hermes ?? {});
const hermesPurchases = computed(() => hermes.value?.compras ?? []);
const hasHermesPurchases = computed(() => hermesPurchases.value.length > 0);
const hermesTotals = computed(() =>
  Object.entries(hermes.value?.total_pago ?? {}).filter(
    ([, cents]) => Number(cents) > 0
  )
);
const hermesFirstTouch = computed(
  () => hermes.value?.origem?.primeiro_toque ?? {}
);
const hermesLastTouch = computed(
  () => hermes.value?.origem?.ultimo_toque ?? {}
);
const hasHermesOrigin = computed(
  () =>
    Object.keys(hermesFirstTouch.value).length > 0 ||
    Object.keys(hermesLastTouch.value).length > 0
);
const hermesTouches = computed(() =>
  [
    {
      touchKey: 'first',
      touch: hermesFirstTouch.value,
      labelKey: 'FIRST_TOUCH',
    },
    {
      touchKey: 'last',
      touch: hermesLastTouch.value,
      labelKey: 'LAST_TOUCH',
    },
  ].filter(({ touch }) => Object.keys(touch).length > 0)
);
const showOriginSection = computed(
  () => hasOrigin.value || hasHermesOrigin.value
);
const hermesPages = computed(() => hermes.value?.paginas ?? []);
const hasHermesSite = computed(
  () => hermesPages.value.length > 0 || Boolean(hermes.value?.paginas_total)
);
const hermesJourneyUrl = computed(() => {
  const url = hermes.value?.jornada_url;
  return url && isValidURL(url) ? url : '';
});

const formatDateTime = value => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString(resolvedLocale.value, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMoney = (cents, currency) => {
  const amount = Number(cents);
  if (!Number.isFinite(amount)) return '';
  const code = currency || 'BRL';
  try {
    return new Intl.NumberFormat(resolvedLocale.value, {
      style: 'currency',
      currency: code,
    }).format(amount / 100);
  } catch {
    return `${code} ${(amount / 100).toFixed(2)}`;
  }
};

const PURCHASE_STATUS = {
  pago: { key: 'PAID', tone: 'text-n-teal-11' },
  recusado: { key: 'DECLINED', tone: 'text-n-ruby-11' },
  estornado: { key: 'REFUNDED', tone: 'text-n-ruby-11' },
  pendente: { key: 'PENDING', tone: 'text-n-amber-11' },
  vencido: { key: 'EXPIRED', tone: 'text-n-amber-11' },
};

const purchaseStatus = compra => {
  const entry = PURCHASE_STATUS[compra.status];
  if (!entry) return null;
  const key =
    compra.status === 'vencido' && compra.metodo === 'pix'
      ? 'PIX_EXPIRED'
      : entry.key;
  return {
    label: t(`LEAD_CONTEXT.HERMES.STATUS.${key}`),
    tone: entry.tone,
  };
};

const PURCHASE_METHODS = {
  pix: 'PIX',
  credit_card: 'CARD',
  boleto: 'BOLETO',
  stripe: 'STRIPE',
  shopify: 'SHOPIFY',
  hotmart: 'HOTMART',
};

const purchaseMethod = compra => {
  if (!compra.metodo) return '';
  const key = PURCHASE_METHODS[compra.metodo];
  const label = key ? t(`LEAD_CONTEXT.HERMES.METHOD.${key}`) : compra.metodo;
  if (compra.metodo === 'credit_card' && Number(compra.parcelas) > 1) {
    return `${label} ${compra.parcelas}x`;
  }
  return label;
};

const purchaseMeta = compra =>
  [
    purchaseMethod(compra),
    formatDateTime(compra.ocorreu_em),
    compra.site || compra.loja,
  ].filter(Boolean);

const META_SOURCES = ['facebook', 'instagram', 'fb', 'ig', 'meta'];
const TOUCH_CHANNELS = {
  email: 'EMAIL',
  organico: 'ORGANIC',
  whatsapp: 'WHATSAPP',
  direto: 'DIRECT',
};

const touchChannel = touch => {
  if (!touch?.canal) return '';
  if (touch.canal === 'anuncio') {
    const source = String(touch.utm_source || '').toLowerCase();
    if (META_SOURCES.includes(source)) {
      return t('LEAD_CONTEXT.HERMES.CHANNEL.AD_META');
    }
    if (source === 'google') return t('LEAD_CONTEXT.HERMES.CHANNEL.AD_GOOGLE');
    return t('LEAD_CONTEXT.HERMES.CHANNEL.AD');
  }
  const key = TOUCH_CHANNELS[touch.canal];
  return key ? t(`LEAD_CONTEXT.HERMES.CHANNEL.${key}`) : touch.canal;
};

const touchSummary = touch =>
  [formatDateTime(touch?.ocorreu_em), touchChannel(touch)]
    .filter(Boolean)
    .join(' · ');

const TOUCH_CHIP_KEYS = ['utm_source', 'utm_medium', 'utm_content'];
const touchChips = touch =>
  TOUCH_CHIP_KEYS.filter(key => touch?.[key]).map(key => [key, touch[key]]);

const siteVisits = computed(() => {
  const count = Number(hermes.value?.paginas_total);
  if (!count) return '';
  const days = Number(hermes.value?.paginas_dias);
  return days
    ? t('LEAD_CONTEXT.HERMES.SITE.VISITS_IN_DAYS', { count, days })
    : t('LEAD_CONTEXT.HERMES.SITE.VISITS', { count });
});

const fetchContext = async () => {
  requestId += 1;
  const currentRequest = requestId;
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await LeadContextAPI.get(conversationId.value);
    if (currentRequest !== requestId) return;
    context.value = data;
    isLoaded.value = true;
  } catch {
    if (currentRequest !== requestId) return;
    hasError.value = true;
  } finally {
    if (currentRequest === requestId) isLoading.value = false;
  }
};

const toggle = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value && !isLoaded.value && !isLoading.value) fetchContext();
};

watch(conversationId, () => {
  // Invalida a busca em voo antes de zerar o estado.
  requestId += 1;
  isOpen.value = false;
  isLoading.value = false;
  isLoaded.value = false;
  hasError.value = false;
  context.value = null;
});
</script>

<template>
  <div v-if="isVisible" class="flex w-full flex-col" data-testid="lead-context">
    <button
      type="button"
      data-testid="lead-context-toggle"
      class="flex h-[22px] w-full shrink-0 items-center justify-center gap-1 border-t border-n-weak text-[11px] font-medium text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
      :aria-expanded="isOpen"
      @click="toggle"
    >
      <span
        class="i-ri-arrow-down-s-line size-3.5 transition-transform"
        :class="{ 'rotate-180': isOpen }"
        aria-hidden="true"
      />
      {{ t('LEAD_CONTEXT.TOGGLE') }}
    </button>

    <div
      v-if="isOpen"
      data-testid="lead-context-body"
      class="flex flex-col gap-4 border-t border-n-weak px-4 py-3 md:flex-row md:gap-6"
    >
      <p v-if="isLoading" class="m-0 text-xs text-n-slate-11">
        {{ t('LEAD_CONTEXT.LOADING') }}
      </p>

      <p
        v-else-if="hasError"
        data-testid="lead-context-error"
        class="m-0 flex flex-wrap items-baseline gap-2 text-xs text-n-slate-11"
      >
        {{ t('LEAD_CONTEXT.ERROR') }}
        <button
          type="button"
          data-testid="lead-context-retry"
          class="font-medium text-n-blue-11 hover:underline"
          @click="fetchContext"
        >
          {{ t('LEAD_CONTEXT.RETRY') }}
        </button>
      </p>

      <p
        v-else-if="isEmpty"
        data-testid="lead-context-empty"
        class="m-0 text-xs text-n-slate-11"
      >
        {{ t('LEAD_CONTEXT.EMPTY') }}
      </p>

      <section
        v-if="showOriginSection"
        class="flex min-w-0 flex-1 flex-col gap-1.5"
      >
        <p
          class="m-0 text-[11px] font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('LEAD_CONTEXT.ORIGIN') }}
        </p>
        <div
          v-if="originChannel"
          class="flex min-w-0 items-baseline gap-2 text-xs"
        >
          <span class="shrink-0 text-n-slate-11">
            {{ t('LEAD_CONTEXT.ORIGIN_CHANNEL') }}
          </span>
          <span class="min-w-0 flex-1 truncate text-right text-n-slate-12">
            {{ originChannel }}
          </span>
        </div>
        <div
          v-if="origin.campaign"
          class="flex min-w-0 items-baseline gap-2 text-xs"
        >
          <span class="shrink-0 text-n-slate-11">
            {{ t('LEAD_CONTEXT.ORIGIN_CAMPAIGN') }}
          </span>
          <span
            class="min-w-0 flex-1 truncate text-right text-n-slate-12"
            :title="origin.campaign"
          >
            {{ origin.campaign }}
          </span>
        </div>
        <a
          v-if="origin.ad_headline && adUrl"
          :href="adUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="min-w-0 truncate text-xs font-medium text-n-blue-11 hover:underline"
          :title="origin.ad_headline"
        >
          {{ origin.ad_headline }}
        </a>
        <span
          v-else-if="origin.ad_headline"
          class="min-w-0 truncate text-xs text-n-slate-12"
          :title="origin.ad_headline"
        >
          {{ origin.ad_headline }}
        </span>
        <div v-if="utmEntries.length" class="flex flex-wrap gap-1">
          <span
            v-for="[key, value] in utmEntries"
            :key="key"
            :title="`${key}: ${value}`"
            class="max-w-full truncate rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] text-n-slate-11"
          >
            {{ value }}
          </span>
        </div>
        <span v-if="origin.captured_at" class="text-[11px] text-n-slate-10">
          {{ t('LEAD_CONTEXT.ORIGIN_CAPTURED_AT') }}
          {{ formatDate(origin.captured_at) }}
        </span>
        <div
          v-if="hasHermesOrigin"
          data-testid="lead-context-hermes-origin"
          class="flex min-w-0 flex-col gap-1.5"
          :class="{ 'border-t border-n-weak pt-1.5': hasOrigin }"
        >
          <div
            v-for="{ touchKey, touch, labelKey } in hermesTouches"
            :key="touchKey"
            :data-testid="`lead-context-hermes-${touchKey}-touch`"
            class="flex min-w-0 flex-col gap-1"
          >
            <div class="flex min-w-0 items-baseline gap-2 text-xs">
              <span class="shrink-0 text-n-slate-11">
                {{ t(`LEAD_CONTEXT.HERMES.ORIGIN.${labelKey}`) }}
              </span>
              <span
                class="min-w-0 flex-1 truncate text-right text-n-slate-12"
                :title="touchSummary(touch)"
              >
                {{ touchSummary(touch) }}
              </span>
            </div>
            <div
              v-if="touchKey === 'first' && touch.utm_campaign"
              class="flex min-w-0 items-baseline gap-2 text-xs"
            >
              <span class="shrink-0 text-n-slate-11">
                {{ t('LEAD_CONTEXT.ORIGIN_CAMPAIGN') }}
              </span>
              <span
                class="min-w-0 flex-1 truncate text-right text-n-slate-12"
                :title="touch.utm_campaign"
              >
                {{ touch.utm_campaign }}
              </span>
            </div>
            <div
              v-if="touchKey === 'first' && touch.pagina"
              class="flex min-w-0 items-baseline gap-2 text-xs"
            >
              <span class="shrink-0 text-n-slate-11">
                {{ t('LEAD_CONTEXT.HERMES.ORIGIN.LANDING_PAGE') }}
              </span>
              <span
                class="min-w-0 flex-1 truncate text-right text-n-slate-12"
                :title="touch.pagina"
              >
                {{ touch.pagina }}
              </span>
            </div>
            <div v-if="touchChips(touch).length" class="flex flex-wrap gap-1">
              <span
                v-for="[chipKey, chipValue] in touchChips(touch)"
                :key="chipKey"
                :title="`${chipKey}: ${chipValue}`"
                class="max-w-full truncate rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] text-n-slate-11"
              >
                {{ chipValue }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section
        v-if="activity.length"
        class="flex min-w-0 flex-1 flex-col gap-1.5"
      >
        <p
          class="m-0 text-[11px] font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('LEAD_CONTEXT.ACTIVITY') }}
        </p>
        <div
          v-for="item in activity"
          :key="item.id"
          class="flex min-w-0 items-baseline gap-2 text-xs"
        >
          <span
            class="min-w-0 flex-1 truncate text-n-slate-12"
            :title="item.label"
          >
            {{ item.label }}
          </span>
          <span class="shrink-0 tabular-nums text-n-slate-10">
            {{ formatDate(item.occurred_at) }}
          </span>
        </div>
      </section>

      <section
        v-if="journey.length"
        class="flex min-w-0 flex-1 flex-col gap-1.5"
      >
        <p
          class="m-0 text-[11px] font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('LEAD_CONTEXT.JOURNEY') }}
        </p>
        <div
          v-for="event in journey"
          :key="event.id"
          class="flex min-w-0 items-baseline gap-2 text-xs"
        >
          <span
            class="min-w-0 flex-1 truncate text-n-slate-12"
            :title="event.summary"
          >
            {{ event.summary }}
          </span>
          <span class="shrink-0 tabular-nums text-n-slate-10">
            {{ formatDate(event.occurred_at) }}
          </span>
        </div>
      </section>

      <section
        v-if="hasMembership"
        data-testid="lead-context-membership"
        class="flex min-w-0 flex-1 flex-col gap-1.5"
      >
        <p
          class="m-0 text-[11px] font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('LEAD_CONTEXT.MEMBERSHIP.TITLE') }}
        </p>
        <div
          v-for="plan in membershipPlans"
          :key="plan.titulo"
          class="flex min-w-0 items-baseline gap-2 text-xs"
        >
          <span
            class="min-w-0 flex-1 truncate text-n-slate-12"
            :title="plan.titulo"
          >
            {{ plan.titulo }}
          </span>
          <span class="shrink-0 tabular-nums text-n-slate-10">
            {{ membershipStatus(plan) }}
            <template v-if="membershipExpiry(plan)">
              · {{ membershipExpiry(plan) }}
            </template>
          </span>
        </div>
      </section>

      <section
        v-if="hasPurchases"
        data-testid="lead-context-hotmart"
        class="flex min-w-0 flex-1 flex-col gap-1.5"
      >
        <p
          class="m-0 text-[11px] font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('LEAD_CONTEXT.PURCHASES.TITLE') }}
        </p>
        <div
          v-for="compra in purchaseRows"
          :key="`${compra.produto}-${compra.plano || ''}`"
          class="flex min-w-0 flex-col gap-0.5 text-xs"
        >
          <div class="flex min-w-0 items-baseline gap-2">
            <span
              class="min-w-0 flex-1 truncate text-n-slate-12"
              :title="compra.produto"
            >
              {{ compra.produto }}
            </span>
            <span v-if="compra.plano" class="shrink-0 text-n-slate-10">
              {{ compra.plano }}
            </span>
          </div>
          <span
            v-if="compra.proxima_cobranca"
            class="text-[11px] text-n-slate-10"
          >
            {{ t('LEAD_CONTEXT.PURCHASES.NEXT_CHARGE') }}
            {{ formatDate(compra.proxima_cobranca) }}
          </span>
        </div>
      </section>

      <section
        v-if="hasHermesPurchases"
        data-testid="lead-context-hermes-purchases"
        class="flex min-w-0 flex-1 flex-col gap-1.5"
      >
        <p
          class="m-0 text-[11px] font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('LEAD_CONTEXT.HERMES.PURCHASES.TITLE') }}
        </p>
        <div
          v-for="(compra, index) in hermesPurchases"
          :key="`${compra.ocorreu_em || ''}-${compra.produto || compra.rotulo}-${index}`"
          data-testid="lead-context-hermes-purchase"
          class="flex min-w-0 flex-col gap-0.5 text-xs"
        >
          <div class="flex min-w-0 items-baseline gap-2">
            <span
              class="min-w-0 flex-1 truncate text-n-slate-12"
              :title="compra.rotulo || compra.produto"
            >
              {{ compra.rotulo || compra.produto }}
            </span>
            <span
              v-if="compra.valor_centavos != null"
              class="shrink-0 tabular-nums text-n-slate-12"
            >
              {{ formatMoney(compra.valor_centavos, compra.moeda) }}
            </span>
          </div>
          <span
            class="flex min-w-0 flex-wrap items-baseline gap-x-1 text-[11px] text-n-slate-10"
          >
            <span
              v-if="purchaseStatus(compra)"
              data-testid="lead-context-hermes-purchase-status"
              class="font-medium"
              :class="purchaseStatus(compra).tone"
            >
              {{ purchaseStatus(compra).label }}
            </span>
            <template
              v-for="(part, partIndex) in purchaseMeta(compra)"
              :key="partIndex"
            >
              <span
                v-if="partIndex > 0 || purchaseStatus(compra)"
                aria-hidden="true"
              >
                ·
              </span>
              <span class="min-w-0 truncate">{{ part }}</span>
            </template>
          </span>
        </div>
        <div
          v-if="hermesTotals.length"
          data-testid="lead-context-hermes-total"
          class="flex min-w-0 items-baseline gap-2 border-t border-n-weak pt-1.5 text-xs"
        >
          <span class="shrink-0 text-n-slate-11">
            {{ t('LEAD_CONTEXT.HERMES.PURCHASES.TOTAL_PAID') }}
          </span>
          <span
            class="min-w-0 flex-1 truncate text-right font-medium tabular-nums text-n-slate-12"
          >
            {{
              hermesTotals
                .map(([currency, cents]) => formatMoney(cents, currency))
                .join(' · ')
            }}
          </span>
        </div>
      </section>

      <section
        v-if="hasHermesSite"
        data-testid="lead-context-hermes-site"
        class="flex min-w-0 flex-1 flex-col gap-1.5"
      >
        <p
          class="m-0 text-[11px] font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('LEAD_CONTEXT.HERMES.SITE.TITLE') }}
        </p>
        <div
          v-for="(pagina, index) in hermesPages.slice(0, 5)"
          :key="`${pagina.url || pagina.titulo}-${index}`"
          data-testid="lead-context-hermes-page"
          class="flex min-w-0 items-baseline gap-2 text-xs"
        >
          <span
            class="min-w-0 flex-1 truncate text-n-slate-12"
            :title="pagina.url || pagina.titulo"
          >
            {{ pagina.titulo || pagina.url }}
          </span>
          <span class="shrink-0 tabular-nums text-n-slate-10">
            {{ formatDateTime(pagina.ocorreu_em) }}
          </span>
        </div>
        <span v-if="siteVisits" class="text-[11px] text-n-slate-10">
          {{ siteVisits }}
        </span>
        <a
          v-if="hermesJourneyUrl"
          data-testid="lead-context-hermes-journey-link"
          :href="hermesJourneyUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 text-xs font-medium text-n-blue-11 hover:underline"
        >
          {{ t('LEAD_CONTEXT.HERMES.SITE.FULL_JOURNEY') }}
          <span class="i-lucide-external-link size-3" aria-hidden="true" />
        </a>
      </section>

      <section
        v-if="hasDisc"
        data-testid="lead-context-disc"
        class="flex min-w-0 flex-1 flex-col gap-1.5"
      >
        <p
          class="m-0 text-[11px] font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('LEAD_CONTEXT.DISC.TITLE') }}
        </p>
        <div class="flex min-w-0 items-baseline gap-2 text-xs">
          <span
            class="shrink-0 font-semibold text-n-slate-12"
            data-testid="lead-context-disc-letter"
          >
            {{ disc.type }}
          </span>
          <span class="min-w-0 flex-1 truncate text-n-slate-11">
            {{ disc.label }}
          </span>
        </div>
        <p v-if="disc.tip" class="m-0 text-xs text-n-slate-12">
          {{ disc.tip }}
        </p>
      </section>

      <section
        v-if="hasGuia"
        data-testid="lead-context-guia"
        class="flex min-w-0 flex-1 flex-col gap-1.5"
      >
        <p
          class="m-0 text-[11px] font-bold uppercase tracking-wide text-n-slate-11"
        >
          {{ t('LEAD_CONTEXT.GUIA.TITLE') }}
        </p>
        <div
          v-if="guia.icp?.label"
          class="flex min-w-0 items-baseline gap-2 text-xs"
        >
          <span class="shrink-0 text-n-slate-11">
            {{ t('LEAD_CONTEXT.GUIA.ICP') }}
          </span>
          <span class="min-w-0 flex-1 truncate text-right text-n-slate-12">
            {{ guia.icp.label }}
          </span>
        </div>
        <div
          v-if="guia.icp?.temperatura"
          class="flex min-w-0 items-baseline gap-2 text-xs"
        >
          <span class="shrink-0 text-n-slate-11">
            {{ t('LEAD_CONTEXT.GUIA.TEMPERATURE') }}
          </span>
          <span class="min-w-0 flex-1 truncate text-right text-n-slate-12">
            {{ guia.icp.temperatura }}
          </span>
        </div>
        <div v-if="guia.icp?.evidencias?.length" class="flex flex-wrap gap-1">
          <span
            v-for="evidencia in guia.icp.evidencias"
            :key="evidencia"
            class="max-w-full truncate rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] text-n-slate-11"
          >
            {{ evidencia }}
          </span>
        </div>
        <div
          v-for="(item, index) in guiaHistorico"
          :key="`${item.tipo}-${index}`"
          class="flex min-w-0 items-baseline gap-2 text-xs"
        >
          <span
            class="min-w-0 flex-1 truncate text-n-slate-12"
            :title="item.resumo"
          >
            {{ item.resumo }}
          </span>
          <span class="shrink-0 tabular-nums text-n-slate-10">
            {{ formatDate(item.occurred_at) }}
          </span>
        </div>
      </section>
    </div>
  </div>
</template>
